// Maps an AI-applied filter object into the Redux `localFilter` records the
// left panel / filter tags read, so AI-applied filters appear "applied" exactly
// like a manual search. Mirrors the record shape every filter panel produces:
//   { key: <category>, id: <field label>, name: <display>, apiKey: <field>,
//     [field]: value, removeID: <field>, showALLDisplayValue: <display> }
// Range fields (xxxMin/xxxMax) collapse into one record keyed by the base field,
// matching how PropertyDetails/ValueEquity/etc. build them.
//
// Category keys are hardcoded (mirroring leftPanelKeys in ConstantsNew) so this
// module stays dependency-free and safe to import from the data/search layer.
const K = {
  LeadLists: 'll',
  PropertyDetails: 'pb',
  PropStreamIntelligence: 'pi',
  MLS: 'mls',
  PreForeclosureBankedOwned: 'pfbo',
  OwnerInformationOccupancy: 'oio',
  LienBankruptcyDivorce: 'lbd',
  ValueEquity: 've',
  Mortgage: 'm',
}

// Inlined copy of utils/filter_menu/minandmax generateMinMaxValue (kept here to
// avoid a data/search -> app/Search import cycle).
function generateMinMaxValue(minValue, maxValue) {
  const hasMin = minValue !== undefined && minValue !== null && minValue !== ''
  const hasMax = maxValue !== undefined && maxValue !== null && maxValue !== ''
  if (hasMin && hasMax) return `${minValue} to ${maxValue}`
  if (hasMin) return `${minValue} or more`
  if (hasMax) return `${maxValue} or fewer`
  return ''
}

// ── Value label maps ────────────────────────────────────────────────────────
export const LISTING_TYPE_LABELS = {
  M: 'On Market', V: 'Vacant', L: 'Liens', P: 'Pre-Foreclosures', A: 'Auctions',
  F: 'Bank Owned', C: 'Cash Buyers', E: 'High Equity', R: 'Free & Clear',
  B: 'Bankruptcy', D: 'Divorce', X: 'Tax Delinquency', I: 'Flippers',
  Z: 'Failed Listings', S: 'Senior Owners', VLN: 'Vacant Land', UD: 'Upside Down',
  T: 'Tired Landlords', ZOM: 'Zombie Properties', DEC: 'Pre-Probate',
}
export const PROP_CLASS_LABELS = { R: 'Residential', C: 'Commercial', V: 'Vacant Land' }
export const ENUM_LABELS = {
  OUT_OF_STATE: 'Out of State', OUT_OF_COUNTY: 'Out of County', LOCAL: 'Local',
  INDIVIDUAL: 'Individual', CORPORATE: 'Corporate', MIXED: 'Mixed', TRUST: 'Trust',
  PRE_FORECLOSURE: 'Pre-Foreclosure', AUCTION: 'Auction', RECISSION: 'Rescission',
  FORECLOSURE: 'Foreclosure', NONE: 'None', ACTIVE: 'Active', PENDING: 'Pending',
  SOLD: 'Sold', EXPIRED: 'Expired', CANCELED: 'Canceled', WITHDRAWN: 'Withdrawn',
  CONTINGENT: 'Contingent', COMING_SOON: 'Coming Soon',
  ACTIVE_UNDER_CONTRACT: 'Active Under Contract',
}

// Field (or range-base) → { cat: left-panel category key, label, fmt }.
// fmt: how a range value bound is formatted ('money' | 'percent' | 'rate' | undefined).
const FIELD_META = {
  // Lead Lists
  listingType: { cat: K.LeadLists, label: 'Lead Lists' },
  // Property Details
  propertyClassCode: { cat: K.PropertyDetails, label: 'Property Class' },
  bedrooms: { cat: K.PropertyDetails, label: 'Bedrooms' },
  bathrooms: { cat: K.PropertyDetails, label: 'Bathrooms' },
  squareFeet: { cat: K.PropertyDetails, label: 'Building Sqft' },
  lotSquareFeet: { cat: K.PropertyDetails, label: 'Lot Sqft' },
  yearBuilt: { cat: K.PropertyDetails, label: 'Year Built' },
  units: { cat: K.PropertyDetails, label: 'Units' },
  stories: { cat: K.PropertyDetails, label: 'Stories' },
  hoaPresent: { cat: K.PropertyDetails, label: 'HOA' },
  poolAvailable: { cat: K.PropertyDetails, label: 'Pool' },
  garageAvailable: { cat: K.PropertyDetails, label: 'Garage' },
  basementAvailable: { cat: K.PropertyDetails, label: 'Basement' },
  atticAvailable: { cat: K.PropertyDetails, label: 'Attic' },
  // Value & Equity
  estimatedValue: { cat: K.ValueEquity, label: 'Estimated Value', fmt: 'money' },
  estimatedEquity: { cat: K.ValueEquity, label: 'Estimated Equity', fmt: 'money' },
  estimatedEquityPercent: { cat: K.ValueEquity, label: 'Estimated Equity %', fmt: 'percent' },
  loanToValueRatio: { cat: K.ValueEquity, label: 'Loan to Value', fmt: 'percent' },
  assessedValue: { cat: K.ValueEquity, label: 'Assessed Value', fmt: 'money' },
  estimatedRentalIncome: { cat: K.ValueEquity, label: 'Estimated Rent', fmt: 'money' },
  wholesaleValue: { cat: K.ValueEquity, label: 'Wholesale Value', fmt: 'money' },
  grossYield: { cat: K.ValueEquity, label: 'Gross Yield', fmt: 'percent' },
  // Owner Information & Occupancy
  ownerOccupied: { cat: K.OwnerInformationOccupancy, label: 'Owner Occupied' },
  vacant: { cat: K.OwnerInformationOccupancy, label: 'Vacant' },
  ownershipLength: { cat: K.OwnerInformationOccupancy, label: 'Ownership Length' },
  ownerType: { cat: K.OwnerInformationOccupancy, label: 'Owner Type' },
  ownerLocation: { cat: K.OwnerInformationOccupancy, label: 'Owner Location' },
  ownerDeceased: { cat: K.OwnerInformationOccupancy, label: 'Owner Deceased' },
  saleAmount: { cat: K.OwnerInformationOccupancy, label: 'Last Sale Amount', fmt: 'money' },
  propertiesOwned: { cat: K.OwnerInformationOccupancy, label: 'Properties Owned' },
  // MLS
  forSale: { cat: K.MLS, label: 'For Sale' },
  rental: { cat: K.MLS, label: 'For Rent' },
  mlsListingStatus: { cat: K.MLS, label: 'MLS Status' },
  daysOnMarket: { cat: K.MLS, label: 'Days on Market' },
  mlsListingAmount: { cat: K.MLS, label: 'MLS Listing Amount', fmt: 'money' },
  belowMarketPrice: { cat: K.MLS, label: 'Below Market Price' },
  // Pre-Foreclosure & Bank Owned
  foreclosureStatus2: { cat: K.PreForeclosureBankedOwned, label: 'Foreclosure Status' },
  openingBidAmount: { cat: K.PreForeclosureBankedOwned, label: 'Opening Bid', fmt: 'money' },
  defaultAmount: { cat: K.PreForeclosureBankedOwned, label: 'Default Amount', fmt: 'money' },
  // Lien, Bankruptcy & Divorce
  lienActive: { cat: K.LienBankruptcyDivorce, label: 'Liens' },
  bankruptcyActive: { cat: K.LienBankruptcyDivorce, label: 'Bankruptcy' },
  divorceActive: { cat: K.LienBankruptcyDivorce, label: 'Divorce' },
  lienAmount: { cat: K.LienBankruptcyDivorce, label: 'Lien Amount', fmt: 'money' },
  delinquentTaxYear: { cat: K.LienBankruptcyDivorce, label: 'Delinquent Tax Year' },
  // Mortgage
  cashBuyer: { cat: K.Mortgage, label: 'Cash Buyer' },
  freeClear: { cat: K.Mortgage, label: 'Free & Clear' },
  openMortgageBalance: { cat: K.Mortgage, label: 'Open Mortgage Balance', fmt: 'money' },
  mortgageCount: { cat: K.Mortgage, label: 'Mortgage Count' },
  mortgageInterestRate: { cat: K.Mortgage, label: 'Mortgage Rate', fmt: 'rate' },
  mortgage1InterestRate: { cat: K.Mortgage, label: '1st Mortgage Rate', fmt: 'rate' },
  mortgageTotalPayment: { cat: K.Mortgage, label: 'Mortgage Payment', fmt: 'money' },
  mortgage1LtvRatio: { cat: K.Mortgage, label: '1st Mortgage LTV', fmt: 'percent' },
  // PropStream Intelligence (condition scores)
  propCondition: { cat: K.PropStreamIntelligence, label: 'Property Condition' },
  bathCondition: { cat: K.PropStreamIntelligence, label: 'Bath Condition' },
  kitchenCondition: { cat: K.PropStreamIntelligence, label: 'Kitchen Condition' },
  intCondition: { cat: K.PropStreamIntelligence, label: 'Interior Condition' },
  extCondition: { cat: K.PropStreamIntelligence, label: 'Exterior Condition' },
  foreclosureFactor: { cat: K.PropStreamIntelligence, label: 'Foreclosure Factor' },
}

// ── Value formatting ────────────────────────────────────────────────────────
function formatMoney(v) {
  const n = Number(v)
  if (isNaN(n)) return String(v)
  if (n >= 1e6) return `$${(n / 1e6).toFixed(n % 1e6 ? 1 : 0)}M`
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`
  return `$${n}`
}
function formatBound(value, fmt) {
  if (value === undefined || value === null || value === '') return ''
  if (fmt === 'money') return formatMoney(value)
  if (fmt === 'percent') return `${value}%`
  if (fmt === 'rate') return `${(Number(value) * 100).toFixed(2).replace(/\.?0+$/, '')}%`
  return String(value)
}
function humanizeKey(key) {
  return key
    .replace(/(Min|Max)$/, '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}
function valueLabel(field, value) {
  if (field === 'listingType') return LISTING_TYPE_LABELS[value] || String(value)
  if (field === 'propertyClassCode') return PROP_CLASS_LABELS[value] || String(value)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'string' && ENUM_LABELS[value]) return ENUM_LABELS[value]
  return String(value)
}

/**
 * Human-readable label for a chat filter chip (shared with the left-panel tags
 * so wording is consistent).
 */
export function chipLabel(key, value) {
  if (key === 'listingType') return LISTING_TYPE_LABELS[value] || `Type: ${value}`
  if (key === 'propertyClassCode') return PROP_CLASS_LABELS[value] || `Class: ${value}`
  if (value === true) return humanizeKey(key)
  if (value === false) return `No ${humanizeKey(key)}`

  const isMin = /Min$/.test(key)
  const isMax = /Max$/.test(key)
  const base = key.replace(/(Min|Max)$/, '')
  const label = (FIELD_META[base] && FIELD_META[base].label) || humanizeKey(key)

  if (isMin || isMax) {
    const fmt = FIELD_META[base] && FIELD_META[base].fmt
    const display = formatBound(value, fmt) || value
    return isMin ? `${label} ${display}+` : `${label} ≤ ${display}`
  }
  if (typeof value === 'string' && ENUM_LABELS[value]) return `${label}: ${ENUM_LABELS[value]}`
  return `${label}: ${value}`
}

// Some PropStream filter fields don't work in isolation — they need companion
// "class"/"included" flags that the UI sets alongside them. The AI is told to
// avoid these (prompt prefers the Lead List presets), but if it sets one anyway
// we complete the coupling here so the filter actually narrows, mirroring the
// dispatches the manual UI control fires.
//
// IMPORTANT: before exposing any new field to the AI, check its panel in
// app/Search/Header/searchFilterNew/panel/right/* — if selecting it dispatches
// MORE than one updateFilter (a companion Class/Status/Included field) or uses a
// comma-joined multi-select value, add the coupling here (or exclude the field).
export function completeCouplings(filters = {}) {
  const out = { ...filters }
  // foreclosureStatus2 ("Pre-Foreclosure Types") is gated by foreclosureStatus2Class
  // (Include toggle) + preforeclosureIncluded — see PreForeclosureBank.js.
  if (out.foreclosureStatus2 && out.foreclosureStatus2Class == null) {
    out.foreclosureStatus2Class = 'PreForeclosure'
    out.preforeclosureIncluded = true
  }
  return out
}

/**
 * Build the localFilter records (+ optional favorite item) for an AI filter set.
 * @param {object} filters  Flat filter field map as applied by the AI.
 * @returns {{ records: Array, favoriteItem: object|null }}
 */
export function buildAiFilterTags(filters = {}) {
  const records = []
  const handledBases = new Set()
  let favoriteItem = null

  Object.keys(filters).forEach((key) => {
    const value = filters[key]
    if (value === undefined || value === null || value === '') return

    const isRange = /(Min|Max)$/.test(key)
    const base = key.replace(/(Min|Max)$/, '')
    const meta = FIELD_META[isRange ? base : key]

    if (isRange) {
      if (handledBases.has(base)) return
      handledBases.add(base)
      const fmt = meta && meta.fmt
      const name = generateMinMaxValue(formatBound(filters[`${base}Min`], fmt), formatBound(filters[`${base}Max`], fmt))
      if (!name) return
      const cat = meta ? meta.cat : K.PropertyDetails
      const label = meta ? meta.label : humanizeKey(base)
      records.push({ key: cat, id: label, name, apiKey: base, removeID: base, showALLDisplayValue: name })
      return
    }

    const cat = meta ? meta.cat : K.PropertyDetails
    const label = meta ? meta.label : humanizeKey(key)
    const name = valueLabel(key, value)

    if (key === 'listingType') {
      favoriteItem = { value, label: name }
      records.push({
        key: cat, id: label, name, option: { value, label: name },
        apiKey: 'listingType', removeID: 'listingType', showALLDisplayValue: name,
      })
      return
    }

    records.push({ key: cat, id: label, name, apiKey: key, [key]: value, removeID: key, showALLDisplayValue: name })
  })

  return { records, favoriteItem }
}
